import type { DiagnosticFinding } from "../diagnostics/types";

import { classifyRepairAuthority } from "./repairAuthorityClassifier";

import { planRepairVerification } from "./repairVerificationPlanner";

import type { RepairProposal } from "./types";

function capabilitiesFor(finding: DiagnosticFinding): string[] {
  const capabilities = ["project-intake", "analysis"];

  if (finding.requiresMutation) {
    capabilities.push("mutation-planning", "tests");
  }

  if (finding.requiresLiveVerification) {
    capabilities.push("runtime-observation");
  }

  if (finding.category === "deployment") {
    capabilities.push("cloudflare-read");
  }

  if (finding.category === "database") {
    capabilities.push("supabase-read");
  }

  return [...new Set(capabilities)];
}

function workersFor(capabilities: readonly string[]): string[] {
  const mapping: Record<string, string> = {
    "project-intake": "cyan-project-intake",

    analysis: "cyan-reasoning",

    "mutation-planning": "mutation-planner",

    tests: "terminal-worker",

    "runtime-observation": "observability-runtime",

    "cloudflare-read": "cloudflare-read-adapter",

    "supabase-read": "supabase-read-adapter",
  };

  return [
    ...new Set(
      capabilities
        .map((capability) => mapping[capability])
        .filter((value): value is string => Boolean(value)),
    ),
  ];
}

export function generateRepairProposal(
  finding: DiagnosticFinding,
): RepairProposal {
  const classification = classifyRepairAuthority(finding);

  const capabilities = capabilitiesFor(finding);

  const evidencePaths = finding.evidence.map((evidence) => evidence.path);

  return {
    id: `repair-${finding.id}`,

    findingId: finding.id,

    title: finding.title,

    objective: finding.recommendedAction,

    affectedPaths: [...finding.affectedPaths],

    evidencePaths,

    severity: finding.severity,

    risk: classification.risk,

    authority: classification.authority,

    reversible: classification.reversible,

    rollbackStrategy: classification.reversible
      ? "Restore the pre-change repository checkpoint and re-run verification."
      : "Privileged or irreversible repair requires an explicit provider-specific rollback plan before execution.",

    requiredCapabilities: capabilities,

    preferredWorkers: workersFor(capabilities),

    dependencies: [],

    verification: planRepairVerification(finding),

    requiresApproval: classification.requiresApproval,

    status: classification.requiresApproval ? "approval_required" : "ready",
  };
}
